import './AttachmentsPreview.scss';
import { prefixClass } from 'lib/utils';

import React, { FC, useState, useMemo } from 'react';

import type { AttachmentsPreviewExtra } from 'presentation/components/KSQ2/Cards/MessagesCard/subcomponents/MessageContent/analyzeMessageContent';
import MessageAttachmentModal from 'presentation/components/Queue/common/MessageAttachments/MessageAttachmentModal/MessageAttachmentModal';
import { Attachment } from 'presentation/components/Queue/Chat/Chat';
import { IconFile, IconDocument } from 'components/Icons';
import AttachmentThumbnail from './AttachmentThumbnail';

type PrefixClassName = (element?: string) => string;

export interface AttachmentsPreviewProps {
  attachments: AttachmentsPreviewExtra['attachments'];
}

const getFileIcon = (fileType: string | undefined) => {
  if (!fileType) return <IconFile />;
  const type = fileType.toLowerCase();

  // Use IconDocument for all document types for now
  if (type === 'pdf' || type === 'xls' || type === 'xlsx' || type === 'csv' || type === 'doc' || type === 'docx') {
    return <IconDocument />;
  }

  return <IconFile />;
};

const isImageType = (fileType: string | undefined): boolean => {
  if (!fileType) return false;
  const imageTypes = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'tiff', 'svg'];
  return imageTypes.includes(fileType.toLowerCase());
};

const AttachmentsPreview: FC<AttachmentsPreviewProps> = ({ attachments }) => {
  const prefix = prefixClass('q2-message-preview-module-attachments');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Show all attachments in the grid - let CSS handle the layout
  const displayAttachments = attachments;
  const remainingCount = 0; // No longer limiting display

  const handleAttachmentClick = (index: number) => {
    setSelectedIndex(index);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      <div className={prefix()}>
        {displayAttachments.map((attachment, index) => {
          const isImage = isImageType(attachment.fileType);

          return (
            <div key={attachment.attachmentId} className={prefix('item')} onClick={() => handleAttachmentClick(index)}>
              {isImage ? (
                <AttachmentThumbnail attachment={attachment} />
              ) : (
                <div className={prefix('item-document')}>
                  <div className={prefix('item-document-icon')}>{getFileIcon(attachment.fileType)}</div>
                  <div className={prefix('item-document-info')}>
                    <div className={prefix('item-document-name')}>{attachment.filename}</div>
                    <div className={prefix('item-document-type')}>{attachment.fileType?.toUpperCase()}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {modalOpen && (
        <MessageAttachmentModal attachments={attachments.slice(selectedIndex)} handleClose={handleCloseModal} />
      )}
    </>
  );
};

export default AttachmentsPreview;
